import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { AxiosResponseWithRefreshToken } from 'src/models/GalenData/Request.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useAutoConnectCamera from 'src/hooks/useAutoConnectCamera';
import useSetCurrentUserInfo from 'src/hooks/useSetCurrentUserInfo';
import useUiTranslation from 'src/hooks/useUiTranslation';

import LoginContainer from './LoginContainer';
import MFALogin from './MFALogin';
import PasswordLogin from './PasswordLogin';
import ResetPasswordForm from './ResetPasswordForm';

const LoginForm = () => {
  const { tPages, tCommon, language } = useUiTranslation();

  const [shouldShowOTPInput, setShouldShowOTPInput] = useState<boolean>(false);
  const [shouldShowResetPassword, setShouldShowResetPassword] =
    useState<boolean>(false);
  const [userId, setUserId] = useState<string>('');
  const [currentPassword, setCurrentPassword] = useState<string>('');

  const { practiceSettings } = useRemoteBoundStore();

  const { setRememberUserEmail, isRememberEmail } = useLocalStore();

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const history = useHistory();

  const [xRequestToken, setXRequestToken] = useState('');

  const validateUserRole = useCallback(
    (
      res: AxiosResponseWithRefreshToken<GalenDataUser>,
      callback?: () => void,
    ) => {
      const userRole = res.data?.currentRole?.role;

      if (
        userRole &&
        ![UserRoleEnum.PracticeUser, UserRoleEnum.PracticeAdmin].includes(
          userRole,
        )
      ) {
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: tPages('loginLogout.accessDenied', { userRole }),
          locale: getLocaleCode(language as Language),
          type: 'error',
          callback,
        });
        return false;
      }
      return true;
    },
    [tPages, tCommon, language],
  );

  const { setCurrentUserInfo } = useSetCurrentUserInfo();

  const { autoConnectCamera } = useAutoConnectCamera();

  const handleVerificationSuccess = async (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
  ) => {
    await setCurrentUserInfo(res);
    autoConnectCamera(practiceSettings);
    setShouldShowOTPInput(false);

    if (res.data?.passwordExpired) {
      setUserId(res.data.userId);
      setShouldShowResetPassword(true);
      return;
    }

    if (isRememberEmail) {
      setRememberUserEmail(res.data.emailAddress);
    } else {
      setRememberUserEmail(undefined);
    }

    history.replace('/');
  };

  return (
    <LoginContainer>
      {shouldShowOTPInput && (
        <MFALogin
          xRequestToken={xRequestToken}
          validateUserRole={validateUserRole}
          onVerificationSuccess={handleVerificationSuccess}
          setIsLoggingIn={setIsLoggingIn}
          isLoggingIn={isLoggingIn}
        />
      )}

      {shouldShowResetPassword && (
        <ResetPasswordForm userId={userId} currentPassword={currentPassword} />
      )}

      {!shouldShowOTPInput && !shouldShowResetPassword && (
        <PasswordLogin
          isLoggingIn={isLoggingIn}
          setIsLoggingIn={setIsLoggingIn}
          setCurrentPassword={setCurrentPassword}
          setXRequestToken={setXRequestToken}
          setShouldShowOTPInput={setShouldShowOTPInput}
          validateUserRole={validateUserRole}
          setShouldShowResetPassword={setShouldShowResetPassword}
          setUserId={setUserId}
          practiceSettings={practiceSettings}
        />
      )}
    </LoginContainer>
  );
};

export default LoginForm;
